import { featuredWorkoutsLeadIn } from './featured-workouts-lead-in'
import { FeaturedWorkoutCard } from './featured-workout-card'
import { AuthSplitHeroPanel } from '../auth/util/auth-split-hero-panel'
import { WorkoutCardSkeleton } from '../ui/workout-card/workout-card-skeleton'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { FeaturedWorkout } from 'src/dao/core-dao'
import { useFeaturedShelfViewed } from 'src/hook/use-featured-shelf-viewed'
import { useFeaturedWorkouts } from 'src/hook/use-featured-workouts'
import { MixpanelEvent, mixpanelService } from 'src/service/mixpanel-service'

/**
 * The lower band's skeleton count. Matches the mosaic's own shape — one lead over a two-up row —
 * so the loading state reserves the boxes the loaded state will use.
 */
const SKELETON_TAIL_COUNT = 2

/**
 * The left half of the sign-in page: the free workouts, in place of the trainer photograph.
 *
 * A drop-in replacement for `AuthSplitHeroPanel` — same half-width, full-height frame, same
 * closing copy — shared by the email step and the OTP step, so someone waiting for their code
 * is still looking at workouts.
 *
 * **Why this and not the shelf below the form.** Both halves of the sign-in split are
 * `min-h-screen`, so anything appended underneath is guaranteed to start below the fold. The
 * only way a free workout is visible without scrolling is for it to occupy space the page
 * already has, and the photograph is the space it has.
 *
 * **Why a mosaic and not a stack.** The card system fixes `aspect-[16/9]`. Three full-width
 * cards stacked in a 720px column is 1215px tall — taller than the viewport, which would
 * reintroduce the scroll. One lead card over a two-up row divides a fixed 100vh instead, with
 * the cards sized by height (`fill`), so the panel fits any viewport and any set size.
 *
 * **An empty set falls back to the photograph.** The sign-in page is the app's front door: it
 * must never show a blank half-screen because the featured set was cleared. This is the same
 * rule the shelf follows — an empty set renders nothing rather than a placeholder — expressed
 * for a surface that owns half the page and so cannot render nothing.
 *
 * **Loading renders the mosaic in skeletons**, not the photograph. Handing back to the
 * photograph on every load meant the front door showed the pre-feature barbell shot and then
 * swapped it for workouts a moment later — a visible flash of the old design on the app's most
 * visited public page. Skeletons keep the panel's own shape from the first paint, and because
 * the frame below is shared, the loading and loaded states occupy exactly the same boxes.
 */
export const FeaturedWorkoutsHeroPanel = (): React.ReactElement => {
  const { featuredWorkouts, isLoading } = useFeaturedWorkouts()
  const navigate = useNavigate()
  useFeaturedShelfViewed({ surface: 'sign-in', workoutCount: featuredWorkouts.length })

  if (!isLoading && featuredWorkouts.length === 0) return <AuthSplitHeroPanel />

  const play = (workout: FeaturedWorkout): void => {
    mixpanelService.trackFeatured(MixpanelEvent.FEATURED_WORKOUT_PLAY_CLICKED, {
      workoutId: workout.id,
      workoutTitle: workout.title,
      surface: 'sign-in',
      signedIn: false,
    })
    navigate(`/auth/featured/${workout.id}`)
  }

  // Rendered in the order the server gave them — shortest to longest. The lead card is the
  // shortest class, which is also the easiest one to say yes to. Do not sort.
  //
  // While loading the set is still empty, so `lead` is `undefined` — which is why each band
  // branches on `isLoading` rather than on `lead` being present.
  const [lead, ...rest] = featuredWorkouts
  const tailCount = isLoading ? SKELETON_TAIL_COUNT : rest.length

  return (
    // `h-dvh` + `overflow-hidden`, not `min-h-dvh`: this panel must never be the reason the page
    // scrolls. Hidden below `lg`, exactly as the photograph panel it replaces was — a phone
    // cannot fit a shelf, a form and a captcha in one screen, so on small screens the shelf
    // stays below the form where scrolling is normal (see `get-in-screen.tsx`).
    //
    // Its own chrome — padding, band gaps, closing copy — is on the same height-relative scale
    // as the sign-in column (GH-908; `get-in-split-layout.tsx` explains it). The panel never
    // overflowed, so this is not about fitting: at a laptop's 740px the chrome was spending
    // ~36px that the cards needed, which is what made the two-up row read as squat. Every
    // ceiling is the value it replaces at 900px tall, so the design target is unchanged.
    <div
      data-testid="featured-workouts-hero-panel"
      className="hidden lg:flex lg:w-1/2 lg:h-dvh relative flex-col gap-4 lg:gap-[clamp(10px,1.8dvh,16px)] overflow-hidden bg-[#020000] p-8 lg:p-[clamp(20px,3.6dvh,32px)]"
    >
      <p className="font-roboto-condensed text-base leading-6 text-white text-center tracking-[0.5px] m-0 shrink-0">
        {featuredWorkoutsLeadIn()}
      </p>

      {/* Height-proportioned rather than fixed — 2fr for the lead, 1fr for the row beneath — so
          the mosaic fills the panel at any viewport height without a media query per breakpoint.
          At the 1440x900 design target that puts both bands near 3:2, so the lead and the pair
          beneath read as one family rather than one wide card over two squat ones. */}
      <div className="flex-[2] min-h-0">
        {isLoading ? (
          // `aspect-auto` for the same reason the card takes `fill`: this band is sized by
          // height, and the skeleton's default `aspect-[16/9]` would overflow it. `cn` is
          // tailwind-merge, so this really does replace the base class.
          <WorkoutCardSkeleton aspectRatio="aspect-auto" />
        ) : (
          <FeaturedWorkoutCard workout={lead} withPreview fill surface="sign-in" onAction={() => play(lead)} />
        )}
      </div>

      {tailCount > 0 ? (
        // Two columns whatever the tail size: the published set is capped at five
        // (`featured-set-util.ts`), so the worst case is a 2x2 grid in the lower band — tighter
        // than intended, but still legible and still inside the viewport.
        //
        // `auto-rows-fr` is load-bearing, not tidiness. An implicit grid row is `auto`, so it
        // sizes to content; the card's only content is an absolutely-positioned image, so the
        // row would measure zero and the cards would vanish. `fr` sizes the row from the band's
        // own height instead, which is what the cards' `h-full` then resolves against.
        <div className="flex-[1] min-h-0 grid grid-cols-2 auto-rows-fr gap-4">
          {isLoading
            ? Array.from({ length: tailCount }, (_, index) => (
                <WorkoutCardSkeleton key={`featured-hero-skeleton-${index}`} aspectRatio="aspect-auto" />
              ))
            : rest.map((workout) => (
                <FeaturedWorkoutCard
                  key={workout.id}
                  workout={workout}
                  withPreview
                  fill
                  surface="sign-in"
                  onAction={() => play(workout)}
                />
              ))}
        </div>
      ) : null}

      <div className="shrink-0">
        <h2 className="font-evogria text-[28px] lg:text-[clamp(22px,3.1dvh,28px)] xl:text-[clamp(24px,4dvh,36px)] text-ivory text-center !mb-2">
          THE HOME OF <span className="text-primary text-inherit font-evogria">STRENGTH TRAINING</span>
        </h2>
        {/* The three-line paragraph is the single biggest piece of chrome in the panel, so it
            carries the scale too — `xl:` is emitted after `lg:`, hence the pair on the heading. */}
        <p className="font-roboto-condensed text-base lg:text-[clamp(13px,1.8dvh,16px)] leading-6 lg:leading-[clamp(17px,2.7dvh,24px)] text-white text-center tracking-[0.5px] m-0">
          Take your at-home training to the next level and enhance your physical and mental health with structured strength-based
          workouts, taught by industry-leading instructors.
        </p>
      </div>
    </div>
  )
}
